import { yupResolver } from '@hookform/resolvers/yup';
import { pageIntroContent } from '@insify/css/classes.module.css';
import { textMd } from '@insify/design-system/typography';
import clsx from 'clsx';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { InputCents } from '../../components/InputMoney';
import { RichContentWithTooltip } from '../../components/RichContent';
import { WizardLayoutFormWithCommonSection } from '../../components/WizardLayoutForm';

import * as styles from './Revenue.module.css';

import type { RichContentType } from '../../components/RichContent';
import type { MoneyField } from '@insify/global-money';
import type { DeepPartial, UseFormReturn } from 'react-hook-form';

export type RevenueFormType = {
  annualRevenue: MoneyField;
};

export type RevenueProps<TFormType extends RevenueFormType> = {
  formData: DeepPartial<TFormType>;
  eventHandlers: {
    onSubmit: (values: TFormType) => void;
    onBack: () => void;
  };
  content?: RichContentType;
  translations: {
    back: string;
    forward: string;
    currency: string;
    validation: {
      type: string;
      required: string;
      min: string;
      max: string;
    };
  };
  renderExtraBlock?: (formMethods: UseFormReturn<TFormType>) => JSX.Element | null;
  submitting: boolean;
  thousandSeparator?: string;
  currencyPosition?: 'before' | 'after';
};

export const Revenue = <TFormType extends RevenueFormType>({
  formData,
  eventHandlers,
  content,
  translations,
  renderExtraBlock,
  submitting,
  thousandSeparator = '.',
  currencyPosition = 'before',
}: RevenueProps<TFormType>): JSX.Element => {
  // Narrow type here a bit to avoid type errors.
  // react-hook-form doesn't work with generics very well.
  // https://github.com/react-hook-form/react-hook-form/issues/6726
  const formMethods = useForm<RevenueFormType>({
    resolver: yupResolver(
      yup.object().shape({
        annualRevenue: yup.object().shape({
          cents: yup
            .number()
            .typeError(translations.validation.type)
            .required(translations.validation.required)
            .min(100, translations.validation.min)
            .max(9999999900, translations.validation.max),
        }),
      }),
    ),
    shouldUnregister: true,
    defaultValues: formData,
    mode: 'onSubmit',
  });
  const { handleSubmit, formState } = formMethods;

  const currencySign = <span className={clsx(textMd, styles.currencySign)}>{translations.currency}</span>;

  return (
    <WizardLayoutFormWithCommonSection
      backCopy={translations.back}
      onBack={eventHandlers.onBack}
      forwardCopy={translations.forward}
      isSubmitting={submitting}
      onSubmit={handleSubmit((values) => eventHandlers.onSubmit(values as TFormType))}
    >
      <RichContentWithTooltip content={content} className={pageIntroContent} />
      <InputCents
        id='annualRevenue'
        {...formMethods.register('annualRevenue.cents')}
        trackingId='annualRevenue'
        decimalScale={0}
        before={currencyPosition === 'before' ? currencySign : undefined}
        after={currencyPosition === 'after' ? currencySign : undefined}
        placeholder='0'
        error={formState.errors.annualRevenue?.cents?.message}
        disabled={submitting}
        thousandSeparator={thousandSeparator}
      />
      {/* @ts-ignore explicit conversion to help react-hook-form */}
      {renderExtraBlock && renderExtraBlock(formMethods as UseFormReturn<TFormType>)}
    </WizardLayoutFormWithCommonSection>
  );
};
